import { TransferItemAPITypes } from '@wetransfer/transfer/transfer-bundle/constant/transfer-item-types';

// Private
let imageCounter = 0;

type CustomFile = Partial<{
  relativePath: string;
  filename: string;
  id: string;
  apiType: keyof typeof TransferItemAPITypes;
  filesize: number;
  previewable: boolean;
}>;

type InternalState = Partial<{
  bytesSent: number;
}>;

export interface ProgressFn {
  (
    { loaded }: ProgressEvent,
    meta: { chunkIndex: number; fileIndex: number }
  ): void;
}

// Public
export default class TransferItem {
  id: string;
  ref: File &
    Partial<{
      metadata: unknown;
      filesize: number;
      mozSlice(): void;
      webkitSlice(): void;
    }>;
  name: string;
  type: string;
  apiType: typeof TransferItemAPITypes[keyof typeof TransferItemAPITypes];
  isFile: boolean;
  size: number;
  safename: string;
  previewable: boolean;
  metadata: unknown;

  state: InternalState;

  buffer?: null | unknown;
  fileIndex?: number;
  onProgress?: ProgressFn;

  constructor(
    original: File & CustomFile,
    progress?: { fileIndex: number; onProgress: ProgressFn }
  ) {
    this.id = original.id || null;
    this.ref = original;
    this.name = original.relativePath || original.name || original.filename;
    this.apiType = original.apiType || TransferItemAPITypes.file;
    this.isFile = this.apiType === TransferItemAPITypes.file;
    this.size = original.size || original.filesize;
    this.safename = this.name.trim();
    this.previewable = original.previewable;
    this.metadata = this.ref.metadata || {};
    this.type = determineType(this);

    // Fix filesize (0 is now treated as undefined)
    if (!this.size) {
      this.size = 0;
    }

    if (progress) {
      const { fileIndex, onProgress } = progress;
      this.fileIndex = fileIndex || 0;
      this.onProgress = onProgress;
    }

    // So, yeah.. iOS is stupid. Or cool, because privacy. I think cool, it's just annoying.
    let extension;

    if (
      !this.id &&
      navigator.userAgent.match(/(iPad|iPhone|iPod)/i) &&
      (extension = this.safename.match(/\.(jpe?g|png|gif|bmp|tiff?)$/i))
    ) {
      this.name = this.safename =
        'image' + `00000${++imageCounter}`.substr(-5) + '.' + extension[1];
    }

    this.state = {} as InternalState;
    resetState(this);
  }

  static resetFilenameCounter(): void {
    imageCounter = 0;
  }

  get bytesSent(): number {
    return this.state.bytesSent;
  }

  didFileSizeChange(): boolean {
    return !(this.validateSize() && this.validateSentBytes());
  }

  validateSentBytes(): boolean {
    return this.state.bytesSent <= this.size;
  }

  // Make sure filesize hasn't changed since file has been added.
  validateSize(): boolean {
    return (
      this.size ===
      (isFinite(this.ref.size) ? this.ref.size : this.ref.filesize)
    );
  }

  // Reset this file state and ID, probably a failed transfer
  reset(): void {
    this.id = null;
    resetState(this);
  }
}

// This method is called during init and reset to
// pretty much just prepare this file for uploading
function resetState(file: TransferItem) {
  file.state = {
    bytesSent: 0,
  };
}

// Return a textual representation of the file
// that is being added. For now just extension.
function determineType(file: TransferItem) {
  // image/*
  // video/*
  // audio/*
  // text/*
  // other

  let ext = file.name;
  ext = ext.substring(ext.lastIndexOf('.') + 1);

  if (!ext) {
    return 'Unknown file';
  }

  return ext.toUpperCase();
}
